'use client';

import { Body, Display, Heading } from '@depop/web-thrift-ui/Typography';

import { HomepageSwitch } from '../HomepageSwitch/HomepageSwitch';
import { TrustSignal } from '../TrustSignal/TrustSignal';

import { DualPropositionHeroButton } from './DualPropositionHeroButton/DualPropositionHeroButton';
import styles from './styles.module.css';

import { ButtonExperiment } from '@/components/ButtonExperiment';
import { ExtendedLink } from '@/components/ExtendedLink';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import {
  CONTROL_IDENTIFIER,
  VARIANT_A_IDENTIFIER,
  VARIANT_B_IDENTIFIER,
} from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { HERO_IMAGE_HEIGHT, HERO_IMAGE_WIDTHS } from '@/modules/home/constants';
import { useDualPropositionContent } from '@/modules/home/hooks/useDualPropositionContent';
import { type DualPropositionHeroContent } from '@/modules/home/types';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function DualPropositionHero({
  heroContent,
}: {
  heroContent?: DualPropositionHeroContent;
}) {
  const [checked, { images, title, description, cta, propositions }] =
    useDualPropositionContent(heroContent);

  const { web_9084_homepage_red_grey_header } = useExperiments([
    'web_9084_homepage_red_grey_header',
  ]);
  const HOMEPAGE_VARIANT = {
    [CONTROL_IDENTIFIER]: null,
    [VARIANT_A_IDENTIFIER]: 'red',
    [VARIANT_B_IDENTIFIER]: checked ? 'grey' : 'red',
  };
  const variant = HOMEPAGE_VARIANT[web_9084_homepage_red_grey_header];
  const isRedGreyExperiment =
    web_9084_homepage_red_grey_header !== CONTROL_IDENTIFIER;

  const [authenticated] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker();
  if (authenticated) {
    return null;
  }

  function handleCtaClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.CTA_ACTION, {
      type: checked ? 'sell_homepage' : 'buy_homepage',
    });
  }

  function handleProtectionClick() {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MODULE_INTERACTION_ACTION,
      {
        interactionType: checked
          ? 'sell_depop_protection'
          : 'buy_depop_protection',
        moduleName: 'homepage_hero',
      }
    );
  }

  return (
    <section
      className={styles.homepageHero}
      data-checked={checked}
      data-variant={variant}
    >
      <div className={styles.heroInner}>
        <div className={styles.heroContent}>
          <HomepageSwitch checked={checked} variant={variant} />
          <div className={styles.heroIntro}>
            {isRedGreyExperiment ? (
              <Display className={styles.heroTitle}>{title}</Display>
            ) : (
              <Heading variant="xl" className={styles.heroTitle}>
                {title}
              </Heading>
            )}
            <Body variant="sm-regular">{description}</Body>
          </div>
          <ExtendedLink href={cta.href} onClick={handleCtaClick}>
            {isRedGreyExperiment ? (
              <DualPropositionHeroButton text={cta.text} variant={variant} />
            ) : (
              <ButtonExperiment
                as="span"
                legacyClassName={styles.heroCtaLegacy}
              >
                {cta.text}
              </ButtonExperiment>
            )}
          </ExtendedLink>
          <div className={styles.trustSignalContainer}>
            {propositions.map(({ description, icon: Icon, title, href }) => (
              <TrustSignal
                key={title}
                title={title}
                icon={Icon}
                description={description}
                href={href}
                onClick={handleProtectionClick}
              />
            ))}
          </div>
        </div>
        <div className={styles.heroImageContainer}>
          <img
            alt={images.alt}
            src={images.mobile}
            srcSet={images.srcSet}
            sizes={images.sizes}
            width={HERO_IMAGE_WIDTHS.mobile}
            height={HERO_IMAGE_HEIGHT}
          />
        </div>
      </div>
    </section>
  );
}
